/**
 * Specialties + cities data — source single of truth for static pages.
 * Replaced by Prisma queries once DB is connected.
 *
 * V2 specialties (pack +33 — paramédicaux, sous-spé, alternatives) sont
 * définis dans specialties-v2.ts et concatés en bout de SPECIALTIES.
 */

import { SPECIALTIES_V2 } from "./specialties-v2";

export type SpecialtySeed = {
  slug: string;
  nameFr: string;
  nameAr: string;
  nameEn: string;
  /**
   * Libellé FR développé, servi dans le <title> des listings quand le nom
   * courant est un acronyme. Mesure SERP du 2026-09-21 sur « orl temara » :
   * les 4 concurrents classés au-dessus de Sahha écrivent tous la forme
   * développée (med.ma « Oto-Rhino-Laryngologiste (ORL) à Témara Maroc »,
   * DabaDoc, docly, nabady) pendant que nous écrivons « ORL » seul.
   * Renseigné uniquement là où nameFr est une abréviation — sinon undefined
   * et c'est nameFr qui sert (cf. lib/text/specialty-label.ts).
   */
  nameFrLong?: string;
  icon: string;
  featured: boolean;
  descriptionFr: string;
  descriptionAr: string;
  descriptionEn: string;
  /** FR canonique (toujours présent). Source pour SearchBar haystack et display FR. */
  commonConditions: string[];
  /**
   * Versions localisées AR/EN — optionnelles, ajoutées progressivement.
   * Reviewer médical AR requis avant production GSC pour les contenus YMYL.
   * Sprint 10 (audit Phase 2 #4) : top 10 specialties traduites. Le reste
   * tombe back sur commonConditions (FR) via getLocalizedConditions().
   */
  commonConditionsAr?: string[];
  commonConditionsEn?: string[];
  averagePriceMad: { min: number; max: number };
};

/**
 * Retourne les conditions dans la locale demandée si disponibles, sinon
 * fallback sur la liste FR canonique. Pattern back-compat : aucune fiche
 * SPECIALTIES sans commonConditions, mais nombre limité avec AR/EN pour
 * l'instant. SearchBar / physicians.ts haystack continuent à inclure
 * TOUTES les locales pour le matching fuzzy multilingue (pas de breaking
 * change sur l'index recherche).
 */
export function getLocalizedConditions(
  spe: Pick<SpecialtySeed, "commonConditions" | "commonConditionsAr" | "commonConditionsEn">,
  locale: string,
): string[] {
  if (locale === "ar" && spe.commonConditionsAr) return spe.commonConditionsAr;
  if (locale === "en" && spe.commonConditionsEn) return spe.commonConditionsEn;
  return spe.commonConditions;
}

export type CitySeed = {
  slug: string;
  nameFr: string;
  nameAr: string;
  nameEn: string;
  region: string;
  featured: boolean;
  lat: number;
  lng: number;
};

export const SPECIALTIES: SpecialtySeed[] = [
  {
    slug: "generaliste",
    nameFr: "Médecin généraliste",
    nameAr: "طبيب عام",
    nameEn: "General practitioner",
    icon: "🩺",
    featured: true,
    descriptionFr:
      "Le médecin généraliste est votre premier interlocuteur santé. Il assure le suivi médical global, établit les diagnostics de premier recours et oriente vers les spécialistes si besoin.",
    descriptionAr:
      "الطبيب العام هو أول مرجع صحي لك. يقوم بالمتابعة الطبية الشاملة، ويضع التشخيصات الأولية، ويوجّهك إلى الأطباء المتخصصين عند الحاجة.",
    descriptionEn:
      "The general practitioner is your first point of contact for health. They provide overall medical follow-up, make first-line diagnoses, and refer you to specialists when needed.",
    commonConditions: ["Grippe", "Fièvre", "Vaccination", "Certificat médical", "Bilan de santé annuel"],
    commonConditionsAr: ["الإنفلونزا", "الحمى", "التلقيح", "شهادة طبية", "الفحص الصحي السنوي"],
    commonConditionsEn: ["Flu", "Fever", "Vaccination", "Medical certificate", "Annual health check"],
    averagePriceMad: { min: 150, max: 300 },
  },
  {
    slug: "cardiologue",
    nameFr: "Cardiologue",
    nameAr: "طبيب القلب",
    nameEn: "Cardiologist",
    icon: "❤️",
    featured: true,
    descriptionFr:
      "Le cardiologue diagnostique et traite les maladies du cœur et des vaisseaux : hypertension, insuffisance cardiaque, arythmie, cholestérol.",
    descriptionAr:
      "طبيب القلب يشخّص ويعالج أمراض القلب والأوعية الدموية: ارتفاع ضغط الدم، قصور القلب، اضطراب نظم القلب، الكوليسترول.",
    descriptionEn:
      "The cardiologist diagnoses and treats diseases of the heart and blood vessels: hypertension, heart failure, arrhythmia, cholesterol.",
    commonConditions: ["Hypertension artérielle", "Insuffisance cardiaque", "Cholestérol", "Palpitations"],
    commonConditionsAr: ["ارتفاع ضغط الدم", "قصور القلب", "الكوليسترول", "الخفقان"],
    commonConditionsEn: ["High blood pressure", "Heart failure", "Cholesterol", "Palpitations"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "dentiste",
    nameFr: "Dentiste",
    nameAr: "طبيب الأسنان",
    nameEn: "Dentist",
    icon: "🦷",
    featured: true,
    descriptionFr:
      "Le dentiste traite les caries, pose couronnes et implants, réalise détartrages et blanchiment. Soins conservateurs, chirurgicaux et esthétiques.",
    descriptionAr:
      "طبيب الأسنان يعالج التسوس، ويركّب التيجان والزرعات، ويقوم بإزالة الجير والتبييض. علاجات محافظة وجراحية وتجميلية.",
    descriptionEn:
      "The dentist treats cavities, fits crowns and implants, performs scaling and whitening. Provides conservative, surgical and cosmetic care.",
    commonConditions: ["Carie", "Détartrage", "Couronne", "Implant", "Blanchiment", "Chirurgie orale", "Dent de sagesse"],
    commonConditionsAr: ["تسوس الأسنان", "تنظيف الأسنان", "تاج الأسنان", "زرع الأسنان", "تبييض الأسنان", "جراحة الفم", "ضرس العقل"],
    commonConditionsEn: ["Cavity", "Scaling", "Crown", "Dental implant", "Whitening", "Oral surgery", "Wisdom tooth"],
    // 200–6000 MAD était une fourchette d'ACTES (implant, couronne…), pas de
    // consultation : servie telle quelle dans le title, la meta description,
    // la FAQ prix et le priceRange JSON-LD des 55 listings dentiste, elle
    // annonçait « tarifs 200–6000 MAD » pour un rendez-vous. Le détail acte
    // par acte reste sur /actes/[acte]/[ville] (données actes-seo.ts).
    averagePriceMad: { min: 150, max: 400 },
  },
  {
    slug: "dermatologue",
    nameFr: "Dermatologue",
    nameAr: "طبيب جلدية",
    nameEn: "Dermatologist",
    icon: "✨",
    featured: true,
    descriptionFr:
      "Le dermatologue s'occupe des maladies de la peau, des cheveux et des ongles : acné, eczéma, psoriasis, mélanome, soins esthétiques.",
    descriptionAr:
      "طبيب الأمراض الجلدية يعتني بأمراض الجلد والشعر والأظافر: حب الشباب، الإكزيما، الصدفية، الميلانوم، والعناية التجميلية.",
    descriptionEn:
      "The dermatologist treats diseases of the skin, hair and nails: acne, eczema, psoriasis, melanoma, and cosmetic care.",
    commonConditions: ["Acné", "Eczéma", "Psoriasis", "Allergie cutanée", "Contrôle grains de beauté"],
    commonConditionsAr: ["حب الشباب", "الإكزيما", "الصدفية", "الحساسية الجلدية", "فحص الشامات"],
    commonConditionsEn: ["Acne", "Eczema", "Psoriasis", "Skin allergy", "Mole check"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "gynecologue",
    nameFr: "Gynécologue",
    nameAr: "طبيب النساء والتوليد",
    nameEn: "Gynecologist",
    icon: "🌸",
    featured: true,
    descriptionFr:
      "Le gynécologue assure le suivi de la santé féminine : contraception, grossesse, ménopause, dépistage cancers féminins.",
    descriptionAr:
      "طبيب النساء والتوليد يتابع الصحة النسائية: وسائل منع الحمل، الحمل، سن اليأس، الكشف المبكر عن السرطانات النسائية.",
    descriptionEn:
      "The gynaecologist provides follow-up for women's health: contraception, pregnancy, menopause, screening for female cancers.",
    commonConditions: ["Suivi de grossesse", "Contraception", "Ménopause", "Endométriose"],
    commonConditionsAr: ["متابعة الحمل", "وسائل منع الحمل", "سن اليأس", "بطانة الرحم المهاجرة"],
    commonConditionsEn: ["Pregnancy follow-up", "Contraception", "Menopause", "Endometriosis"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "pediatre",
    nameFr: "Pédiatre",
    nameAr: "طبيب أطفال",
    nameEn: "Pediatrician",
    icon: "👶",
    featured: true,
    descriptionFr:
      "Le pédiatre assure le suivi médical des enfants de la naissance à l'adolescence : vaccinations, croissance, maladies infantiles.",
    descriptionAr:
      "طبيب الأطفال يتابع الرعاية الطبية للأطفال من الولادة إلى المراهقة: التلقيحات، النمو، أمراض الطفولة.",
    descriptionEn:
      "The paediatrician provides medical follow-up for children from birth to adolescence: vaccinations, growth, childhood illnesses.",
    commonConditions: ["Vaccination", "Suivi de croissance", "Otites", "Bronchiolite"],
    commonConditionsAr: ["التلقيح", "متابعة النمو", "التهاب الأذن", "التهاب الشعب الهوائية"],
    commonConditionsEn: ["Vaccination", "Growth follow-up", "Ear infections", "Bronchiolitis"],
    averagePriceMad: { min: 300, max: 500 },
  },
  {
    slug: "ophtalmologue",
    nameFr: "Ophtalmologue",
    nameAr: "طبيب العيون",
    nameEn: "Ophthalmologist",
    icon: "👁️",
    featured: true,
    descriptionFr:
      "L'ophtalmologue examine la vue, prescrit lunettes et lentilles, traite glaucome, cataracte, DMLA.",
    descriptionAr:
      "طبيب العيون يفحص البصر، ويصف النظارات والعدسات، ويعالج الزرق والساد والتنكس البقعي المرتبط بالعمر.",
    descriptionEn:
      "The ophthalmologist examines vision, prescribes glasses and lenses, treats glaucoma, cataracts and AMD.",
    commonConditions: ["Correction visuelle", "Cataracte", "Glaucome", "Conjonctivite"],
    commonConditionsAr: ["تصحيح البصر", "إعتام عدسة العين", "الجلوكوما", "التهاب الملتحمة"],
    commonConditionsEn: ["Vision correction", "Cataract", "Glaucoma", "Conjunctivitis"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "orl",
    nameFr: "ORL",
    nameFrLong: "Oto-rhino-laryngologiste (ORL)",
    nameAr: "طبيب الأنف والأذن والحنجرة",
    nameEn: "ENT",
    icon: "👂",
    featured: true,
    descriptionFr:
      "L'ORL traite les affections de l'oreille, du nez et de la gorge : sinusite, otite, acouphènes, apnée du sommeil.",
    descriptionAr:
      "طبيب الأنف والأذن والحنجرة يعالج أمراض الأذن والأنف والحنجرة: التهاب الجيوب، التهاب الأذن، الطنين، انقطاع النفس أثناء النوم.",
    descriptionEn:
      "The ENT specialist treats conditions of the ear, nose and throat: sinusitis, otitis, tinnitus, sleep apnoea.",
    commonConditions: ["Sinusite", "Otite", "Angine", "Apnée du sommeil"],
    commonConditionsAr: ["التهاب الجيوب الأنفية", "التهاب الأذن", "التهاب الحلق", "انقطاع النفس النومي"],
    commonConditionsEn: ["Sinusitis", "Ear infection", "Sore throat", "Sleep apnea"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "psychiatre",
    nameFr: "Psychiatre",
    nameAr: "طبيب نفسي",
    nameEn: "Psychiatrist",
    icon: "🧠",
    featured: true,
    descriptionFr:
      "Le psychiatre est médecin spécialisé en santé mentale. Il diagnostique, prescrit médicaments et accompagne les troubles psychiques.",
    descriptionAr:
      "الطبيب النفسي طبيب متخصص في الصحة النفسية. يقوم بالتشخيص، ويصف الأدوية، ويرافق المرضى في الاضطرابات النفسية.",
    descriptionEn:
      "The psychiatrist is a doctor specialised in mental health. They diagnose, prescribe medication and support patients with psychiatric disorders.",
    commonConditions: ["Dépression", "Anxiété", "Trouble bipolaire", "Burn-out"],
    commonConditionsAr: ["الاكتئاب", "القلق", "اضطراب ثنائي القطب", "الإرهاق النفسي"],
    commonConditionsEn: ["Depression", "Anxiety", "Bipolar disorder", "Burn-out"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "psychologue",
    nameFr: "Psychologue",
    nameAr: "أخصائي نفسي",
    nameEn: "Psychologist",
    icon: "💭",
    featured: true,
    descriptionFr:
      "Le psychologue accompagne par la parole les difficultés psychologiques : anxiété, deuil, couple, addictions.",
    descriptionAr:
      "الأخصائي النفسي يرافق المرضى بالحوار في صعوباتهم النفسية: القلق، الحداد، الحياة الزوجية، الإدمان.",
    descriptionEn:
      "The psychologist provides talk-based support for psychological difficulties: anxiety, bereavement, couples, addictions.",
    commonConditions: ["Anxiété", "Dépression légère", "Thérapie de couple", "Stress"],
    commonConditionsAr: ["القلق", "اكتئاب خفيف", "علاج الأزواج", "التوتر"],
    commonConditionsEn: ["Anxiety", "Mild depression", "Couple therapy", "Stress"],
    averagePriceMad: { min: 250, max: 500 },
  },
  {
    slug: "rhumatologue",
    nameFr: "Rhumatologue",
    nameAr: "طبيب الروماتيزم",
    nameEn: "Rheumatologist",
    icon: "🦴",
    featured: true,
    descriptionFr:
      "Le rhumatologue traite les maladies des os, articulations, muscles et tendons : arthrose, arthrite, ostéoporose, lombalgies.",
    descriptionAr:
      "طبيب الروماتيزم يعالج أمراض العظام والمفاصل والعضلات والأوتار: الفصال العظمي، التهاب المفاصل، هشاشة العظام، آلام أسفل الظهر.",
    descriptionEn:
      "The rheumatologist treats diseases of the bones, joints, muscles and tendons: osteoarthritis, arthritis, osteoporosis, lower back pain.",
    commonConditions: ["Mal de dos", "Arthrose", "Sciatique", "Ostéoporose", "Tendinite"],
    commonConditionsAr: ["آلام الظهر", "الفصال العظمي", "عرق النسا", "هشاشة العظام", "التهاب الأوتار"],
    commonConditionsEn: ["Back pain", "Osteoarthritis", "Sciatica", "Osteoporosis", "Tendinitis"],
    averagePriceMad: { min: 350, max: 600 },
  },
  {
    slug: "neurologue",
    nameFr: "Neurologue",
    nameAr: "طبيب الأعصاب",
    nameEn: "Neurologist",
    icon: "🧠",
    featured: true,
    descriptionFr:
      "Le neurologue diagnostique et traite les maladies du système nerveux : migraines, épilepsie, AVC, Parkinson, Alzheimer, sclérose en plaques.",
    descriptionAr:
      "طبيب الأعصاب يشخّص ويعالج أمراض الجهاز العصبي: الشقيقة، الصرع، السكتة الدماغية، باركنسون، الزهايمر، التصلب اللويحي.",
    descriptionEn:
      "The neurologist diagnoses and treats nervous-system diseases: migraines, epilepsy, stroke, Parkinson's, Alzheimer's, multiple sclerosis.",
    commonConditions: ["Migraine", "Épilepsie", "AVC", "Parkinson", "Sclérose en plaques"],
    commonConditionsAr: ["الصداع النصفي", "الصرع", "السكتة الدماغية", "باركنسون", "التصلب اللويحي"],
    commonConditionsEn: ["Migraine", "Epilepsy", "Stroke", "Parkinson's", "Multiple sclerosis"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "endocrinologue",
    nameFr: "Endocrinologue",
    nameAr: "طبيب الغدد الصماء",
    nameEn: "Endocrinologist",
    icon: "⚕️",
    featured: true,
    descriptionFr:
      "L'endocrinologue traite les maladies hormonales : diabète, thyroïde, obésité, ménopause, troubles de la croissance.",
    descriptionAr:
      "طبيب الغدد الصماء يعالج الأمراض الهرمونية: السكري، الغدة الدرقية، السمنة، سن اليأس، اضطرابات النمو.",
    descriptionEn:
      "The endocrinologist treats hormonal disorders: diabetes, thyroid, obesity, menopause, growth disorders.",
    commonConditions: ["Diabète", "Hyperthyroïdie", "Hypothyroïdie", "Obésité", "Troubles hormonaux"],
    commonConditionsAr: ["السكري", "فرط نشاط الغدة الدرقية", "قصور الغدة الدرقية", "السمنة", "الاضطرابات الهرمونية"],
    commonConditionsEn: ["Diabetes", "Hyperthyroidism", "Hypothyroidism", "Obesity", "Hormonal disorders"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "gastro-enterologue",
    nameFr: "Gastro-entérologue",
    nameAr: "طبيب الجهاز الهضمي",
    nameEn: "Gastroenterologist",
    icon: "🫃",
    featured: true,
    descriptionFr:
      "Le gastro-entérologue traite les maladies digestives : estomac, intestins, foie, pancréas. Fibroscopie, coloscopie, reflux, Crohn.",
    descriptionAr:
      "طبيب الجهاز الهضمي يعالج أمراض الجهاز الهضمي: المعدة والأمعاء والكبد والبنكرياس. تنظير المعدة، تنظير القولون، الارتجاع، داء كرون.",
    descriptionEn:
      "The gastroenterologist treats digestive diseases: stomach, intestines, liver and pancreas. Gastroscopy, colonoscopy, reflux, Crohn's disease.",
    commonConditions: ["Reflux gastrique", "Syndrome de l'intestin irritable", "Hépatite", "Ulcère", "Coloscopie"],
    commonConditionsAr: ["الارتجاع المعدي", "متلازمة القولون العصبي", "التهاب الكبد", "القرحة", "تنظير القولون"],
    commonConditionsEn: ["Acid reflux", "Irritable bowel syndrome", "Hepatitis", "Ulcer", "Colonoscopy"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "pneumologue",
    nameFr: "Pneumologue",
    nameAr: "طبيب الرئة",
    nameEn: "Pulmonologist",
    icon: "🫁",
    featured: true,
    descriptionFr:
      "Le pneumologue traite les maladies respiratoires : asthme, BPCO, apnée du sommeil, pneumonie, cancer du poumon.",
    descriptionAr:
      "طبيب الرئة يعالج أمراض الجهاز التنفسي: الربو، الانسداد الرئوي المزمن، انقطاع النفس أثناء النوم، الالتهاب الرئوي، سرطان الرئة.",
    descriptionEn:
      "The pulmonologist treats respiratory diseases: asthma, COPD, sleep apnoea, pneumonia, lung cancer.",
    commonConditions: ["Asthme", "BPCO", "Apnée du sommeil", "Bronchite", "Tabagisme"],
    commonConditionsAr: ["الربو", "الانسداد الرئوي المزمن", "انقطاع النفس النومي", "التهاب الشعب الهوائية", "الإقلاع عن التدخين"],
    commonConditionsEn: ["Asthma", "COPD", "Sleep apnea", "Bronchitis", "Smoking cessation"],
    averagePriceMad: { min: 400, max: 600 },
  },
  {
    slug: "urologue",
    nameFr: "Urologue",
    nameAr: "طبيب المسالك البولية",
    nameEn: "Urologist",
    icon: "🧘",
    featured: false,
    descriptionFr:
      "L'urologue traite les maladies de l'appareil urinaire et des organes génitaux masculins : prostate, calculs rénaux, incontinence, infertilité.",
    descriptionAr:
      "طبيب المسالك البولية يعالج أمراض الجهاز البولي والأعضاء التناسلية الذكرية: البروستاتا، حصى الكلى، السلس البولي، العقم.",
    descriptionEn:
      "The urologist treats diseases of the urinary tract and male genital organs: prostate, kidney stones, incontinence, infertility.",
    commonConditions: ["Prostate", "Calculs rénaux", "Incontinence", "Infection urinaire", "Infertilité masculine"],
    commonConditionsAr: ["البروستاتا", "حصى الكلى", "السلس البولي", "التهاب المسالك البولية", "العقم عند الرجل"],
    commonConditionsEn: ["Prostate", "Kidney stones", "Urinary infection", "Incontinence", "Erectile dysfunction"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "orthopediste",
    nameFr: "Orthopédiste",
    nameAr: "جراح العظام",
    nameEn: "Orthopedic surgeon",
    icon: "🦵",
    featured: true,
    descriptionFr:
      "Le chirurgien orthopédiste traite les pathologies des os, articulations, ligaments : fractures, prothèses, ligaments croisés, hernies discales.",
    descriptionAr:
      "جراح العظام يعالج أمراض العظام والمفاصل والأربطة: الكسور، المفاصل الاصطناعية، الرباط الصليبي، الانزلاق الغضروفي.",
    descriptionEn:
      "The orthopaedic surgeon treats conditions of the bones, joints and ligaments: fractures, prostheses, cruciate ligaments, disc herniations.",
    commonConditions: ["Fracture", "Prothèse de hanche", "Ligaments croisés", "Hernie discale", "Entorse"],
    commonConditionsAr: ["الكسر", "استبدال مفصل الورك", "الأربطة الصليبية", "الانزلاق الغضروفي", "الالتواء"],
    commonConditionsEn: ["Fracture", "Hip replacement", "Cruciate ligaments", "Herniated disc", "Sprain"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "kinesitherapeute",
    nameFr: "Kinésithérapeute",
    nameAr: "أخصائي العلاج الطبيعي",
    nameEn: "Physiotherapist",
    icon: "💪",
    featured: true,
    descriptionFr:
      "Le kinésithérapeute rééduque après blessure, chirurgie ou pour traiter mal de dos, tendinites, posture. Massages, exercices, électrothérapie.",
    descriptionAr:
      "أخصائي العلاج الطبيعي يعيد التأهيل بعد الإصابات والعمليات الجراحية، ويعالج آلام الظهر والتهاب الأوتار ومشاكل الوضعية، عبر التدليك والتمارين والعلاج الكهربائي.",
    descriptionEn:
      "The physiotherapist rehabilitates after injury or surgery, and treats back pain, tendinitis and posture issues through massage, exercises and electrotherapy.",
    commonConditions: ["Mal de dos", "Rééducation post-opératoire", "Tendinite", "Torticolis", "Posture"],
    commonConditionsAr: ["آلام الظهر", "إعادة التأهيل بعد العملية", "التهاب الأوتار", "آلام الرقبة", "تصحيح الوضعية"],
    commonConditionsEn: ["Back pain", "Post-op rehab", "Tendinitis", "Stiff neck", "Posture"],
    averagePriceMad: { min: 150, max: 350 },
  },
  {
    slug: "osteopathe",
    nameFr: "Ostéopathe",
    nameAr: "طبيب العظام التقويمي",
    nameEn: "Osteopath",
    icon: "🧘‍♂️",
    featured: false,
    descriptionFr:
      "L'ostéopathe traite par manipulations manuelles : mal de dos, cervicalgies, troubles digestifs, nourrissons, migraines de tension.",
    descriptionAr:
      "أخصائي تقويم العظام يعالج عبر المعالجة اليدوية: آلام الظهر والرقبة، اضطرابات الهضم، مشاكل الرضع، الصداع التوتري.",
    descriptionEn:
      "The osteopath treats with manual techniques: back pain, neck pain, digestive disorders, infants, tension headaches.",
    commonConditions: ["Mal de dos", "Cervicalgie", "Torticolis", "Nourrissons", "Migraine de tension"],
    commonConditionsAr: ["آلام الظهر", "آلام الرقبة", "تشنج الرقبة", "الرضّع", "الصداع التوتري"],
    commonConditionsEn: ["Lower back pain", "Neck pain", "Sciatica", "Headaches", "Joint pain"],
    averagePriceMad: { min: 300, max: 500 },
  },
  {
    slug: "nutritionniste",
    nameFr: "Nutritionniste",
    nameAr: "أخصائي التغذية",
    nameEn: "Nutritionist",
    icon: "🥗",
    featured: true,
    descriptionFr:
      "Le nutritionniste / diététicien conseille en alimentation : perte de poids, diabète, cholestérol, sport, grossesse, troubles du comportement alimentaire.",
    descriptionAr:
      "أخصائي التغذية يقدّم النصح الغذائي: إنقاص الوزن، السكري، الكوليسترول، الرياضة، الحمل، اضطرابات السلوك الغذائي.",
    descriptionEn:
      "The nutritionist / dietitian provides dietary guidance: weight loss, diabetes, cholesterol, sport, pregnancy, eating disorders.",
    commonConditions: ["Perte de poids", "Diabète nutrition", "Cholestérol", "Nutrition sportive", "TCA"],
    commonConditionsAr: ["فقدان الوزن", "تغذية مرضى السكري", "الكوليسترول", "التغذية الرياضية", "اضطرابات الأكل"],
    commonConditionsEn: ["Weight loss", "Diabetes nutrition", "Cholesterol", "Sports nutrition", "Eating disorders"],
    averagePriceMad: { min: 250, max: 500 },
  },
  {
    slug: "sage-femme",
    nameFr: "Sage-femme",
    nameAr: "قابلة",
    nameEn: "Midwife",
    icon: "🤱",
    featured: false,
    descriptionFr:
      "La sage-femme accompagne la grossesse, l'accouchement et le post-partum : suivi, préparation à la naissance, allaitement, rééducation périnéale.",
    descriptionAr:
      "القابلة ترافق الحمل والولادة وما بعد الولادة: المتابعة، التحضير للولادة، الرضاعة، إعادة تأهيل العضلات الحوضية.",
    descriptionEn:
      "The midwife provides support throughout pregnancy, childbirth and the postpartum period: follow-up, birth preparation, breastfeeding, perineal rehabilitation.",
    commonConditions: ["Suivi grossesse", "Accouchement", "Allaitement", "Rééducation périnéale"],
    commonConditionsAr: ["متابعة الحمل", "الولادة", "الرضاعة الطبيعية", "إعادة تأهيل قاع الحوض"],
    commonConditionsEn: ["Pregnancy follow-up", "Childbirth preparation", "Postnatal care", "Breastfeeding"],
    averagePriceMad: { min: 200, max: 400 },
  },
  {
    slug: "oncologue",
    nameFr: "Oncologue",
    nameAr: "طبيب الأورام",
    nameEn: "Oncologist",
    icon: "🎗️",
    featured: false,
    descriptionFr:
      "L'oncologue diagnostique et traite les cancers : chimiothérapie, radiothérapie, thérapies ciblées, accompagnement global.",
    descriptionAr:
      "طبيب الأورام يشخّص ويعالج السرطانات: العلاج الكيميائي، العلاج الإشعاعي، العلاجات الموجّهة، والمرافقة الشاملة.",
    descriptionEn:
      "The oncologist diagnoses and treats cancers: chemotherapy, radiotherapy, targeted therapies and overall support.",
    commonConditions: ["Cancer du sein", "Cancer du côlon", "Cancer du poumon", "Chimiothérapie"],
    commonConditionsAr: ["سرطان الثدي", "سرطان القولون", "سرطان الرئة", "العلاج الكيميائي"],
    commonConditionsEn: ["Cancer", "Chemotherapy", "Tumour", "Cancer follow-up"],
    averagePriceMad: { min: 500, max: 900 },
  },
  {
    slug: "allergologue",
    nameFr: "Allergologue",
    nameAr: "طبيب الحساسية",
    nameEn: "Allergist",
    icon: "🌾",
    featured: false,
    descriptionFr:
      "L'allergologue diagnostique et traite les allergies : rhinite, asthme allergique, allergies alimentaires, eczéma, urticaire. Tests cutanés.",
    descriptionAr:
      "طبيب الحساسية يشخّص ويعالج الحساسيات: التهاب الأنف التحسسي، الربو التحسسي، الحساسيات الغذائية، الإكزيما، الشَّرى. اختبارات جلدية.",
    descriptionEn:
      "The allergist diagnoses and treats allergies: rhinitis, allergic asthma, food allergies, eczema, urticaria. Skin prick tests.",
    commonConditions: ["Rhinite allergique", "Asthme allergique", "Allergie alimentaire", "Urticaire"],
    commonConditionsAr: ["التهاب الأنف التحسسي", "الربو التحسسي", "الحساسية الغذائية", "الشَّرى"],
    commonConditionsEn: ["Allergies", "Asthma", "Eczema", "Allergic rhinitis"],
    averagePriceMad: { min: 350, max: 600 },
  },
  {
    slug: "podologue",
    nameFr: "Podologue",
    nameAr: "طبيب القدم",
    nameEn: "Podiatrist",
    icon: "🦶",
    featured: false,
    descriptionFr:
      "Le podologue traite les pathologies du pied : ongles incarnés, cors, semelles orthopédiques, pied diabétique, fasciite plantaire.",
    descriptionAr:
      "طبيب القدم يعالج أمراض القدم: الظفر الغائر، مسامير اللحم، النعال الطبية، القدم السكرية، التهاب اللفافة الأخمصية.",
    descriptionEn:
      "The podiatrist treats foot conditions: ingrown toenails, corns, orthopaedic insoles, diabetic foot, plantar fasciitis.",
    commonConditions: ["Ongle incarné", "Cors et durillons", "Semelles orthopédiques", "Pied diabétique"],
    commonConditionsAr: ["الظفر الغائر", "مسامير اللحم", "النعال الطبية", "القدم السكرية"],
    commonConditionsEn: ["Plantar warts", "Ingrown toenails", "Calluses", "Foot pain"],
    averagePriceMad: { min: 200, max: 400 },
  },
  {
    slug: "sexologue",
    nameFr: "Sexologue",
    nameAr: "أخصائي الصحة الجنسية",
    nameEn: "Sexologist",
    icon: "💞",
    featured: false,
    descriptionFr:
      "Le sexologue accompagne les difficultés sexuelles et de couple : troubles de l'érection, baisse de libido, douleurs, thérapie de couple.",
    descriptionAr:
      "أخصائي الصحة الجنسية يرافق الصعوبات الجنسية والزوجية: ضعف الانتصاب، تراجع الرغبة الجنسية، الآلام، العلاج الزوجي.",
    descriptionEn:
      "The sexologist supports sexual and couple-related difficulties: erectile dysfunction, low libido, pain, couples therapy.",
    commonConditions: ["Troubles de l'érection", "Baisse de libido", "Dyspareunie", "Thérapie de couple"],
    commonConditionsAr: ["ضعف الانتصاب", "تراجع الرغبة الجنسية", "آلام الجماع", "علاج الأزواج"],
    commonConditionsEn: ["Sexual disorders", "Erectile dysfunction", "Couples therapy", "Loss of libido"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "medecin-du-sport",
    nameFr: "Médecin du sport",
    nameAr: "طبيب الطب الرياضي",
    nameEn: "Sports medicine",
    icon: "⚽",
    featured: false,
    descriptionFr:
      "Le médecin du sport suit les sportifs : blessures, certificats d'aptitude, bilan cardio, préparation physique, dopage.",
    descriptionAr:
      "طبيب الطب الرياضي يتابع الرياضيين: الإصابات، شهادات اللياقة، الفحص القلبي، الإعداد البدني، مكافحة المنشطات.",
    descriptionEn:
      "The sports physician follows athletes: injuries, fitness certificates, cardiac check-ups, physical preparation, anti-doping.",
    commonConditions: ["Blessure sportive", "Certificat d'aptitude", "Bilan cardio-sportif"],
    commonConditionsAr: ["الإصابات الرياضية", "شهادة اللياقة البدنية", "الفحص القلبي الرياضي"],
    commonConditionsEn: ["Sports injuries", "Tendinitis", "Sprains"],
    averagePriceMad: { min: 300, max: 500 },
  },
  {
    slug: "radiologue",
    nameFr: "Radiologue",
    nameAr: "طبيب الأشعة",
    nameEn: "Radiologist",
    icon: "📷",
    featured: false,
    descriptionFr:
      "Le radiologue réalise et interprète les examens d'imagerie : radiographies, échographies, scanner, IRM, mammographie.",
    descriptionAr:
      "طبيب الأشعة يُجري ويفسّر فحوصات التصوير الطبي: التصوير الشعاعي، الإيكوغرافيا، الأشعة المقطعية، الرنين المغناطيسي، تصوير الثدي.",
    descriptionEn:
      "The radiologist performs and interprets imaging examinations: X-rays, ultrasound, CT scans, MRI, mammography.",
    commonConditions: ["Radiographie", "Échographie", "IRM", "Scanner", "Mammographie"],
    commonConditionsAr: ["التصوير الشعاعي", "تصوير بالموجات فوق الصوتية", "الرنين المغناطيسي", "الأشعة المقطعية", "تصوير الثدي"],
    commonConditionsEn: ["X-ray", "Ultrasound", "MRI", "CT scan", "Mammography"],
    averagePriceMad: { min: 250, max: 1500 },
  },
  {
    slug: "infirmier",
    nameFr: "Infirmier(ère)",
    nameAr: "ممرض(ة)",
    nameEn: "Nurse",
    icon: "💊",
    featured: false,
    descriptionFr:
      "L'infirmier(ère) prodigue les soins prescrits par le médecin : injections, pansements, prélèvements, perfusions, suivi des traitements. Soin en cabinet ou en milieu hospitalier.",
    descriptionAr:
      "يقدّم الممرض(ة) العلاجات الموصوفة من قبل الطبيب: الحقن، الضمادات، أخذ العينات، الحقن الوريدي، ومتابعة العلاج. الرعاية في العيادة أو في المستشفى.",
    descriptionEn:
      "The nurse provides care prescribed by the doctor: injections, dressings, blood draws, IVs, treatment follow-up. Care in clinic or hospital setting.",
    commonConditions: ["Injection", "Pansement", "Prise de sang", "Perfusion", "Soins post-opératoires"],
    commonConditionsAr: ["الحقن", "الضمادات", "أخذ عينة دم", "التسريب الوريدي", "الرعاية بعد العملية"],
    commonConditionsEn: ["Insulin injection", "Dressing", "Blood sampling", "Urinary catheter"],
    averagePriceMad: { min: 50, max: 200 },
  },
  {
    slug: "chirurgien-thoracique",
    nameFr: "Chirurgien thoracique",
    nameAr: "جراح الصدر",
    nameEn: "Thoracic surgeon",
    icon: "🫁",
    featured: false,
    descriptionFr: "Le chirurgien thoracique opère les organes du thorax : poumons, œsophage, médiastin, paroi thoracique.",
    descriptionAr:
      "جراح الصدر يُجري عمليات على أعضاء الصدر: الرئتين، المريء، المنصف، جدار الصدر.",
    descriptionEn:
      "The thoracic surgeon operates on organs of the chest: lungs, oesophagus, mediastinum, chest wall.",
    commonConditions: ["Cancer du poumon", "Pneumothorax", "Tumeur médiastinale", "Chirurgie œsophage"],
    commonConditionsAr: ["سرطان الرئة", "استرواح الصدر", "ورم المنصف", "جراحة المريء"],
    commonConditionsEn: ["Lung surgery", "Pleural effusion", "Pneumothorax", "Lung cancer"],
    averagePriceMad: { min: 500, max: 1000 },
  },
  {
    slug: "chirurgien-visceral",
    nameFr: "Chirurgien viscéral",
    nameAr: "جراح الأحشاء",
    nameEn: "Visceral surgeon",
    icon: "🩺",
    featured: false,
    descriptionFr: "Le chirurgien viscéral opère les organes digestifs : estomac, intestin, foie, vésicule, hernies.",
    descriptionAr:
      "جراح الأحشاء يُجري عمليات على أعضاء الجهاز الهضمي: المعدة، الأمعاء، الكبد، المرارة، الفتق.",
    descriptionEn:
      "The visceral surgeon operates on digestive organs: stomach, intestine, liver, gallbladder, hernias.",
    commonConditions: ["Hernie", "Vésicule biliaire", "Appendicite", "Chirurgie bariatrique", "Cancer colorectal"],
    commonConditionsAr: ["الفتق", "المرارة", "التهاب الزائدة الدودية", "جراحة السمنة", "سرطان القولون والمستقيم"],
    commonConditionsEn: ["Appendicitis", "Hernia", "Gallbladder", "Bowel surgery", "Peritonitis"],
    averagePriceMad: { min: 500, max: 900 },
  },
  {
    slug: "chirurgien-general",
    nameFr: "Chirurgien général",
    nameAr: "جراح عام",
    nameEn: "General surgeon",
    icon: "⚕️",
    featured: false,
    descriptionFr: "Le chirurgien général traite pathologies variées : hernies, pathologies digestives, thyroïde.",
    descriptionAr:
      "الجراح العام يعالج أمراضاً متنوعة: الفتق، أمراض الجهاز الهضمي، الغدة الدرقية.",
    descriptionEn:
      "The general surgeon treats a variety of conditions: hernias, digestive disorders, thyroid.",
    commonConditions: ["Hernie", "Thyroïde", "Chirurgie digestive", "Urgence chirurgicale"],
    commonConditionsAr: ["الفتق", "الغدة الدرقية", "جراحة الجهاز الهضمي", "الجراحة الإسعافية"],
    commonConditionsEn: ["Hernia", "Appendicitis", "Gallbladder", "Soft tissue surgery"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "chirurgien-vasculaire",
    nameFr: "Chirurgien vasculaire",
    nameAr: "جراح الأوعية الدموية",
    nameEn: "Vascular surgeon",
    icon: "🩸",
    featured: false,
    descriptionFr: "Le chirurgien vasculaire traite les vaisseaux : artères, veines, lymphatiques. Varices, anévrismes, artériopathies.",
    descriptionAr:
      "جراح الأوعية الدموية يعالج الشرايين والأوردة والأوعية اللمفاوية: الدوالي، تمدد الأوعية، أمراض الشرايين.",
    descriptionEn:
      "The vascular surgeon treats arteries, veins and lymphatics: varicose veins, aneurysms, arterial disease.",
    commonConditions: ["Varices", "Anévrisme", "Artériopathie", "Thrombose veineuse"],
    commonConditionsAr: ["الدوالي", "تمدد الأوعية الدموية", "اعتلال الشرايين", "الخثار الوريدي"],
    commonConditionsEn: ["Varicose veins", "Aneurysm", "Arteritis", "Phlebitis"],
    averagePriceMad: { min: 500, max: 900 },
  },
  {
    slug: "chirurgien-pediatrique",
    nameFr: "Chirurgien pédiatrique",
    nameAr: "جراح الأطفال",
    nameEn: "Pediatric surgeon",
    icon: "👶",
    featured: false,
    descriptionFr: "Le chirurgien pédiatrique opère les enfants de la naissance à l'adolescence.",
    descriptionAr:
      "جراح الأطفال يُجري العمليات الجراحية للأطفال من الولادة إلى المراهقة.",
    descriptionEn:
      "The paediatric surgeon operates on children from birth to adolescence.",
    commonConditions: ["Malformations", "Appendicite enfant", "Hernie pédiatrique", "Phimosis"],
    commonConditionsAr: ["التشوهات الخلقية", "التهاب زائدة الأطفال", "فتق الأطفال", "تضيق القلفة"],
    commonConditionsEn: ["Paediatric hernia", "Cryptorchidism", "Phimosis", "Congenital malformations"],
    averagePriceMad: { min: 500, max: 900 },
  },
  {
    slug: "neurochirurgien",
    nameFr: "Neurochirurgien",
    nameAr: "جراح الأعصاب",
    nameEn: "Neurosurgeon",
    icon: "🧠",
    featured: false,
    descriptionFr: "Le neurochirurgien opère le cerveau, la moelle épinière et les nerfs : tumeurs, hernies discales chirurgicales, anévrismes.",
    descriptionAr:
      "جراح الأعصاب يُجري عمليات على الدماغ والنخاع الشوكي والأعصاب: الأورام، الانزلاق الغضروفي الجراحي، تمدد الأوعية الدماغية.",
    descriptionEn:
      "The neurosurgeon operates on the brain, spinal cord and nerves: tumours, surgical disc herniations, aneurysms.",
    commonConditions: ["Hernie discale", "Tumeur cérébrale", "AVC chirurgical", "Anévrisme cérébral"],
    commonConditionsAr: ["الانزلاق الغضروفي", "ورم دماغي", "السكتة الجراحية", "تمدد الأوعية الدماغية"],
    commonConditionsEn: ["Herniated disc", "Brain tumour", "Hydrocephalus", "Spinal surgery"],
    averagePriceMad: { min: 600, max: 1500 },
  },
  {
    slug: "chirurgien-maxillofacial",
    nameFr: "Chirurgien maxillo-facial",
    nameAr: "جراح الوجه والفكين",
    nameEn: "Maxillofacial surgeon",
    icon: "😬",
    featured: false,
    descriptionFr: "Le chirurgien maxillo-facial opère la face, les mâchoires et la bouche.",
    descriptionAr:
      "جراح الوجه والفكين يُجري عمليات على الوجه والفكين والفم.",
    descriptionEn:
      "The maxillofacial surgeon operates on the face, jaws and mouth.",
    commonConditions: ["Dents de sagesse", "Fracture maxillo-faciale", "Orthognatie", "Tumeur buccale"],
    commonConditionsAr: ["ضرس العقل", "كسور الفكين والوجه", "جراحة الفكين التقويمية", "ورم الفم"],
    commonConditionsEn: ["Jaw surgery", "Facial fracture", "Wisdom teeth", "Facial malformations"],
    averagePriceMad: { min: 500, max: 1200 },
  },
  {
    slug: "anesthesiste",
    nameFr: "Anesthésiste-réanimateur",
    nameAr: "طبيب التخدير والإنعاش",
    nameEn: "Anesthetist",
    icon: "💉",
    featured: false,
    descriptionFr: "L'anesthésiste assure l'anesthésie et la réanimation péri-opératoire. Consultation pré-anesthésique obligatoire avant chirurgie.",
    descriptionAr:
      "طبيب التخدير والإنعاش يضمن التخدير والإنعاش في الفترة المحيطة بالعملية. الاستشارة قبل التخدير إلزامية قبل أي جراحة.",
    descriptionEn:
      "The anaesthetist provides anaesthesia and peri-operative resuscitation. Pre-anaesthetic consultation mandatory before surgery.",
    commonConditions: ["Consultation pré-anesthésique", "Péridurale", "Anesthésie générale"],
    commonConditionsAr: ["استشارة قبل التخدير", "التخدير فوق الجافية", "التخدير العام"],
    commonConditionsEn: ["General anaesthesia", "Locoregional anaesthesia", "Pre-operative consultation"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "nephrologue",
    nameFr: "Néphrologue",
    nameAr: "طبيب الكلى",
    nameEn: "Nephrologist",
    icon: "🫘",
    featured: false,
    descriptionFr: "Le néphrologue traite les maladies rénales : insuffisance rénale, hypertension, dialyse, greffe.",
    descriptionAr:
      "طبيب الكلى يعالج أمراض الكلى: القصور الكلوي، ارتفاع ضغط الدم، غسيل الكلى، الزرع.",
    descriptionEn:
      "The nephrologist treats kidney diseases: renal failure, hypertension, dialysis, transplantation.",
    commonConditions: ["Insuffisance rénale", "Dialyse", "HTA rénale", "Glomérulonéphrite"],
    commonConditionsAr: ["القصور الكلوي", "غسيل الكلى", "ارتفاع الضغط الكلوي", "التهاب الكبيبات الكلوية"],
    commonConditionsEn: ["Kidney failure", "Dialysis", "Kidney stones", "Glomerulonephritis"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "biologiste",
    nameFr: "Biologiste médical",
    nameAr: "أخصائي التحاليل الطبية",
    nameEn: "Medical biologist",
    icon: "🧪",
    featured: false,
    descriptionFr: "Le biologiste analyse sang, urines, prélèvements bactériologiques, sérologies, biologie moléculaire.",
    descriptionAr:
      "أخصائي التحاليل الطبية يحلّل الدم والبول والعينات الجرثومية والتحاليل المصلية والبيولوجيا الجزيئية.",
    descriptionEn:
      "The medical biologist analyses blood, urine, bacteriological samples, serologies and molecular biology.",
    commonConditions: ["Bilan sanguin", "Analyses bactériologiques", "Sérologie", "Prélèvement PCR"],
    commonConditionsAr: ["تحليل الدم", "التحاليل الجرثومية", "التحليل المصلي", "فحص PCR"],
    commonConditionsEn: ["Blood test", "Bacteriology", "Serology", "Medical analyses"],
    averagePriceMad: { min: 50, max: 500 },
  },
  {
    slug: "medecine-interne",
    nameFr: "Médecin interniste",
    nameAr: "طبيب الباطنية",
    nameEn: "Internist",
    icon: "🩺",
    featured: false,
    descriptionFr: "Le médecin interniste prend en charge les pathologies complexes ou multi-organiques de l'adulte.",
    descriptionAr:
      "طبيب الباطنية يتكفل بالأمراض المعقدة والمتعددة الأعضاء عند البالغين.",
    descriptionEn:
      "The internist manages complex and multi-organ diseases in adults.",
    commonConditions: ["Maladies auto-immunes", "Pathologies multi-organiques", "Diagnostic complexe"],
    commonConditionsAr: ["أمراض المناعة الذاتية", "أمراض متعددة الأعضاء", "التشخيص المعقد"],
    commonConditionsEn: ["Complex diagnosis", "Autoimmune diseases", "Polypathology"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "medecine-travail",
    nameFr: "Médecin du travail",
    nameAr: "طبيب الشغل",
    nameEn: "Occupational doctor",
    icon: "💼",
    featured: false,
    descriptionFr: "Le médecin du travail assure le suivi santé des salariés : visites d'embauche, périodiques, aptitude.",
    descriptionAr:
      "طبيب الشغل يتابع صحة الأجراء: فحوصات التشغيل والفحوصات الدورية وتحديد الأهلية للعمل.",
    descriptionEn:
      "The occupational doctor monitors employee health: pre-employment, periodic and fitness-for-work check-ups.",
    commonConditions: ["Visite d'embauche", "Visite périodique", "Certificat d'aptitude", "Maladie professionnelle"],
    commonConditionsAr: ["فحص ما قبل التشغيل", "الفحص الدوري", "شهادة اللياقة المهنية", "الأمراض المهنية"],
    commonConditionsEn: ["Occupational health check", "Fitness for work", "Occupational diseases", "Workplace prevention"],
    averagePriceMad: { min: 200, max: 500 },
  },
  {
    slug: "medecine-reeducation",
    nameFr: "Médecin de rééducation",
    nameAr: "طبيب إعادة التأهيل",
    nameEn: "Rehabilitation doctor",
    icon: "🏃",
    featured: false,
    descriptionFr: "Le médecin de rééducation (MPR) coordonne la rééducation post-AVC, post-traumatique, neurologique.",
    descriptionAr:
      "طبيب إعادة التأهيل (MPR) ينسّق إعادة التأهيل بعد السكتة الدماغية وبعد الإصابات والاضطرابات العصبية.",
    descriptionEn:
      "The rehabilitation physician (PRM) coordinates post-stroke, post-trauma and neurological rehabilitation.",
    commonConditions: ["Post-AVC", "Rééducation neurologique", "Handicap moteur"],
    commonConditionsAr: ["ما بعد السكتة الدماغية", "إعادة التأهيل العصبي", "الإعاقة الحركية"],
    commonConditionsEn: ["Functional rehabilitation", "Post-stroke recovery", "Physical disability"],
    averagePriceMad: { min: 300, max: 600 },
  },
  {
    slug: "anatomopathologiste",
    nameFr: "Anatomopathologiste",
    nameAr: "أخصائي علم الأمراض",
    nameEn: "Pathologist",
    icon: "🔬",
    featured: false,
    descriptionFr: "L'anatomopathologiste analyse biopsies et pièces opératoires pour diagnostic histologique.",
    descriptionAr:
      "أخصائي علم الأمراض يحلل الخزعات والقطع الجراحية لإصدار تشخيص نسيجي.",
    descriptionEn:
      "The pathologist analyses biopsies and surgical specimens to provide a histological diagnosis.",
    commonConditions: ["Biopsie", "Diagnostic cancer", "Cytologie"],
    commonConditionsAr: ["الخزعة", "تشخيص السرطان", "علم الخلايا"],
    commonConditionsEn: ["Biopsy", "Cytology", "Histological analysis"],
    averagePriceMad: { min: 200, max: 800 },
  },
  {
    slug: "orthodontiste",
    nameFr: "Orthodontiste",
    nameAr: "أخصائي تقويم الأسنان",
    nameEn: "Orthodontist",
    icon: "😁",
    featured: false,
    descriptionFr: "L'orthodontiste corrige la position des dents et l'alignement des mâchoires : bagues, gouttières, Invisalign.",
    descriptionAr:
      "أخصائي تقويم الأسنان يصحّح وضعية الأسنان وتراصف الفكين: التقويم المعدني، الجبائر الشفافة، إنفيزالاين.",
    descriptionEn:
      "The orthodontist corrects tooth position and jaw alignment: braces, aligners, Invisalign.",
    commonConditions: ["Appareil dentaire", "Invisalign", "Orthodontie enfant", "Orthodontie adulte"],
    commonConditionsAr: ["تقويم الأسنان", "إنفيزالاين", "تقويم أسنان الأطفال", "تقويم أسنان البالغين"],
    commonConditionsEn: ["Braces", "Dental alignment", "Malocclusion", "Invisalign"],
    averagePriceMad: { min: 3000, max: 25000 },
  },
  {
    slug: "medecin-esthetique",
    nameFr: "Médecin esthétique",
    nameAr: "طبيب التجميل",
    nameEn: "Aesthetic doctor",
    icon: "✨",
    featured: false,
    descriptionFr: "Le médecin esthétique réalise injections (botox, acide hyaluronique), peelings, traitements non-chirurgicaux.",
    descriptionAr:
      "طبيب التجميل يقوم بالحقن (البوتوكس وحمض الهيالورونيك) والتقشير والعلاجات غير الجراحية.",
    descriptionEn:
      "The aesthetic doctor performs injections (botox, hyaluronic acid), peelings and non-surgical treatments.",
    commonConditions: ["Botox", "Acide hyaluronique", "Peeling", "Laser", "PRP"],
    commonConditionsAr: ["البوتوكس", "حمض الهيالورونيك", "التقشير", "الليزر", "حقن البلازما"],
    commonConditionsEn: ["Botox", "Hyaluronic acid", "Peeling", "Laser", "Wrinkles"],
    averagePriceMad: { min: 500, max: 3000 },
  },
  {
    slug: "chirurgien-esthetique",
    nameFr: "Chirurgien esthétique",
    nameAr: "جراح التجميل",
    nameEn: "Plastic surgeon",
    icon: "✂️",
    featured: false,
    descriptionFr:
      "Le chirurgien esthétique réalise interventions de confort : rhinoplastie, augmentation mammaire, liposuccion, lifting, injections.",
    descriptionAr:
      "جراح التجميل يقوم بعمليات تجميلية: تجميل الأنف، تكبير الثدي، شفط الدهون، شد الوجه، الحقن التجميلية.",
    descriptionEn:
      "The plastic surgeon performs cosmetic procedures: rhinoplasty, breast augmentation, liposuction, facelift, injectables.",
    commonConditions: ["Rhinoplastie", "Augmentation mammaire", "Liposuccion", "Greffe de cheveux", "Botox"],
    commonConditionsAr: ["تجميل الأنف", "تكبير الثدي", "شفط الدهون", "زراعة الشعر", "البوتوكس"],
    commonConditionsEn: ["Rhinoplasty", "Liposuction", "Breast augmentation", "Facelift", "Abdominoplasty"],
    averagePriceMad: { min: 500, max: 3000 },
  },
  // ─── Ajouts 2026-05-11 — spécialités présentes en DB (sans URLs canoniques
  //     auparavant) + slugs legacy GSC (opticien, orthophoniste, etc.) qui
  //     généraient des 308 redirects misleading vers /generaliste/. Désormais
  //     servis nativement avec leur propre titre/desc/icon. ─────────────
  {
    slug: "chirurgien",
    nameFr: "Chirurgien",
    nameAr: "جرّاح",
    nameEn: "Surgeon",
    icon: "🔪",
    featured: false,
    descriptionFr:
      "Le chirurgien réalise les interventions chirurgicales générales : appendicite, hernie, vésicule, tumeurs, traumatologie.",
    descriptionAr:
      "الجرّاح يقوم بالعمليات الجراحية العامة: التهاب الزائدة الدودية، الفتق، المرارة، الأورام، الرضوض.",
    descriptionEn:
      "The surgeon performs general surgical procedures: appendicitis, hernia, gallbladder, tumours, trauma.",
    commonConditions: ["Appendicite", "Hernie", "Vésicule biliaire", "Hémorroïdes", "Cholécystectomie"],
    commonConditionsAr: ["التهاب الزائدة الدودية", "الفتق", "المرارة", "البواسير", "استئصال المرارة"],
    commonConditionsEn: ["General surgery", "Appendicitis", "Hernia", "Gallbladder", "Cyst"],
    averagePriceMad: { min: 600, max: 1500 },
  },
  {
    slug: "stomatologue",
    nameFr: "Stomatologue",
    nameAr: "أخصائي الفم والأسنان",
    nameEn: "Stomatologist",
    icon: "👄",
    featured: false,
    descriptionFr:
      "Le stomatologue traite les pathologies de la bouche, des dents, des mâchoires et des glandes salivaires.",
    descriptionAr:
      "أخصائي الفم والأسنان يعالج أمراض الفم والأسنان والفكين والغدد اللعابية.",
    descriptionEn:
      "The stomatologist treats diseases of the mouth, teeth, jaws and salivary glands.",
    commonConditions: ["Aphtes", "Kyste mâchoire", "Glande salivaire", "Dent incluse"],
    commonConditionsAr: ["القلاع الفموي", "كيس الفك", "الغدد اللعابية", "السن المنطمر"],
    commonConditionsEn: ["Oral surgery", "Wisdom teeth", "Dental implant", "Dental cyst"],
    averagePriceMad: { min: 300, max: 1500 },
  },
  {
    slug: "diabetologue",
    nameFr: "Diabétologue",
    nameAr: "أخصائي السكري",
    nameEn: "Diabetologist",
    icon: "🩸",
    featured: false,
    descriptionFr:
      "Le diabétologue diagnostique et suit les patients diabétiques : équilibre glycémique, insulinothérapie, prévention des complications.",
    descriptionAr:
      "أخصائي السكري يشخّص ويتابع مرضى السكري: التوازن الجلوكوزي، العلاج بالأنسولين، الوقاية من المضاعفات.",
    descriptionEn:
      "The diabetologist diagnoses and follows diabetic patients: glycaemic control, insulin therapy, complications prevention.",
    commonConditions: ["Diabète type 1", "Diabète type 2", "Hyperglycémie", "Insulinothérapie", "Pied diabétique"],
    commonConditionsAr: ["السكري من النوع الأول", "السكري من النوع الثاني", "ارتفاع السكر في الدم", "العلاج بالأنسولين", "القدم السكرية"],
    commonConditionsEn: ["Type 1 diabetes", "Type 2 diabetes", "Gestational diabetes", "Diabetes complications", "Insulin"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "hematologue",
    nameFr: "Hématologue",
    nameAr: "أخصائي أمراض الدم",
    nameEn: "Haematologist",
    icon: "🩸",
    featured: false,
    descriptionFr:
      "L'hématologue diagnostique et traite les maladies du sang : anémies, leucémies, lymphomes, troubles de la coagulation.",
    descriptionAr:
      "أخصائي أمراض الدم يشخّص ويعالج أمراض الدم: فقر الدم، اللوكيميا، اللمفومات، اضطرابات التخثر.",
    descriptionEn:
      "The haematologist diagnoses and treats blood diseases: anaemias, leukaemias, lymphomas, coagulation disorders.",
    commonConditions: ["Anémie", "Leucémie", "Lymphome", "Thrombose", "Hémochromatose"],
    commonConditionsAr: ["فقر الدم", "ابيضاض الدم", "اللمفوما", "الخثار", "داء ترسب الأصبغة الدموية"],
    commonConditionsEn: ["Anaemia", "Leukaemia", "Lymphoma", "Coagulation disorders", "Thrombosis"],
    averagePriceMad: { min: 500, max: 1000 },
  },
  {
    slug: "hepatologue",
    nameFr: "Hépatologue",
    nameAr: "أخصائي الكبد",
    nameEn: "Hepatologist",
    icon: "🟤",
    featured: false,
    descriptionFr:
      "L'hépatologue diagnostique et traite les maladies du foie : hépatites, cirrhose, stéatose, cancer du foie.",
    descriptionAr:
      "أخصائي الكبد يشخّص ويعالج أمراض الكبد: الالتهابات الكبدية، تليّف الكبد، الكبد الدهني، سرطان الكبد.",
    descriptionEn:
      "The hepatologist diagnoses and treats liver diseases: hepatitis, cirrhosis, fatty liver, liver cancer.",
    commonConditions: ["Hépatite B", "Hépatite C", "Cirrhose", "Stéatose hépatique"],
    commonConditionsAr: ["التهاب الكبد B", "التهاب الكبد C", "تليّف الكبد", "الكبد الدهني"],
    commonConditionsEn: ["Hepatitis", "Cirrhosis", "Fatty liver", "Liver cancer"],
    averagePriceMad: { min: 500, max: 1000 },
  },
  {
    slug: "chirurgien-cardiaque",
    nameFr: "Chirurgien cardiaque",
    nameAr: "جرّاح القلب",
    nameEn: "Cardiac surgeon",
    icon: "❤️",
    featured: false,
    descriptionFr:
      "Le chirurgien cardiaque réalise les interventions sur le cœur et les vaisseaux : pontages, valves, transplantation.",
    descriptionAr:
      "جرّاح القلب يقوم بعمليات القلب والأوعية: المجازات التاجية، الصمامات، زراعة القلب.",
    descriptionEn:
      "The cardiac surgeon performs operations on the heart and vessels: bypass, valves, transplantation.",
    commonConditions: ["Pontage coronarien", "Valve cardiaque", "Pacemaker", "Anévrisme aortique"],
    commonConditionsAr: ["مجازة الشرايين التاجية", "صمام القلب", "منظم ضربات القلب", "تمدد الأبهر"],
    commonConditionsEn: ["Coronary bypass", "Heart valve", "Aortic aneurysm", "Cardiac malformations"],
    averagePriceMad: { min: 1500, max: 5000 },
  },
  {
    slug: "chirurgien-plasticien",
    nameFr: "Chirurgien plasticien",
    nameAr: "جرّاح التجميل والترميم",
    nameEn: "Plastic and reconstructive surgeon",
    icon: "✂️",
    featured: false,
    descriptionFr:
      "Le chirurgien plasticien réalise reconstructions après traumatisme/cancer et corrections esthétiques.",
    descriptionAr:
      "جرّاح التجميل والترميم يقوم بإعادة البناء بعد الرضوض/السرطان والتصحيحات التجميلية.",
    descriptionEn:
      "The plastic surgeon performs reconstructions after trauma/cancer and aesthetic corrections.",
    commonConditions: ["Reconstruction mammaire", "Brûlures", "Cicatrices", "Malformations"],
    commonConditionsAr: ["إعادة بناء الثدي", "الحروق", "الندوب", "التشوهات الخلقية"],
    commonConditionsEn: ["Reconstructive surgery", "Burns", "Scars", "Congenital malformations"],
    averagePriceMad: { min: 800, max: 3500 },
  },
  {
    slug: "angiologue",
    nameFr: "Angiologue",
    nameAr: "أخصائي الأوعية الدموية",
    nameEn: "Angiologist",
    icon: "🩺",
    featured: false,
    descriptionFr:
      "L'angiologue diagnostique et traite les maladies des artères, veines et lymphatiques.",
    descriptionAr:
      "أخصائي الأوعية الدموية يشخّص ويعالج أمراض الشرايين والأوردة واللمف.",
    descriptionEn:
      "The angiologist diagnoses and treats diseases of arteries, veins and lymphatics.",
    commonConditions: ["Varices", "Phlébite", "Insuffisance veineuse", "Œdème lymphatique"],
    commonConditionsAr: ["الدوالي", "التهاب الوريد الخثاري", "القصور الوريدي", "الوذمة اللمفية"],
    commonConditionsEn: ["Varicose veins", "Phlebitis", "Arteritis", "Lymphoedema"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "geriatre",
    nameFr: "Gériatre",
    nameAr: "أخصائي طب الشيخوخة",
    nameEn: "Geriatrician",
    icon: "👴",
    featured: false,
    descriptionFr:
      "Le gériatre prend en charge les pathologies du sujet âgé : démences, chutes, polypathologies, fragilité.",
    descriptionAr:
      "أخصائي طب الشيخوخة يهتم بأمراض المسنين: الخرف، السقوط، تعدد الأمراض، الهشاشة.",
    descriptionEn:
      "The geriatrician manages diseases of the elderly: dementia, falls, multiple pathologies, frailty.",
    commonConditions: ["Démence", "Alzheimer", "Chutes", "Ostéoporose", "Polymédication"],
    commonConditionsAr: ["الخرف", "الزهايمر", "السقوط", "هشاشة العظام", "تعدد الأدوية"],
    commonConditionsEn: ["Alzheimer's", "Falls", "Dementia", "Polymedication", "Frailty"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "andrologue",
    nameFr: "Andrologue",
    nameAr: "أخصائي طب الذكورة",
    nameEn: "Andrologist",
    icon: "♂️",
    featured: false,
    descriptionFr:
      "L'andrologue diagnostique et traite les troubles de la santé sexuelle et reproductive masculine.",
    descriptionAr:
      "أخصائي طب الذكورة يشخّص ويعالج اضطرابات الصحة الجنسية والإنجابية لدى الرجل.",
    descriptionEn:
      "The andrologist diagnoses and treats male sexual and reproductive health disorders.",
    commonConditions: ["Infertilité masculine", "Dysfonction érectile", "Hypogonadisme", "Varicocèle"],
    commonConditionsAr: ["العقم عند الرجل", "ضعف الانتصاب", "قصور الغدد التناسلية", "دوالي الخصية"],
    commonConditionsEn: ["Male infertility", "Erectile dysfunction", "Andropause", "Testicular disorders"],
    averagePriceMad: { min: 400, max: 800 },
  },
  {
    slug: "pedopsychiatre",
    nameFr: "Pédopsychiatre",
    nameAr: "طبيب نفسي للأطفال",
    nameEn: "Child psychiatrist",
    icon: "🧒",
    featured: false,
    descriptionFr:
      "Le pédopsychiatre diagnostique et traite les troubles psychiques de l'enfant et de l'adolescent.",
    descriptionAr:
      "طبيب الأمراض النفسية للأطفال يشخّص ويعالج الاضطرابات النفسية عند الأطفال والمراهقين.",
    descriptionEn:
      "The child psychiatrist diagnoses and treats mental disorders in children and adolescents.",
    commonConditions: ["TDAH", "Autisme", "Dépression enfant", "Anxiété", "Troubles du comportement"],
    commonConditionsAr: ["فرط الحركة وتشتت الانتباه", "التوحد", "اكتئاب الطفل", "القلق", "اضطرابات السلوك"],
    commonConditionsEn: ["Autism", "ADHD", "Childhood anxiety", "Behavioural disorders", "Childhood depression"],
    averagePriceMad: { min: 400, max: 700 },
  },
  {
    slug: "hypnotherapeute",
    nameFr: "Hypnothérapeute",
    nameAr: "أخصائي العلاج بالتنويم المغناطيسي",
    nameEn: "Hypnotherapist",
    icon: "🌀",
    featured: false,
    descriptionFr:
      "L'hypnothérapeute utilise l'hypnose médicale pour traiter douleurs, anxiété, addictions, phobies, sommeil.",
    descriptionAr:
      "أخصائي العلاج بالتنويم المغناطيسي يستعمل التنويم لمعالجة الآلام، القلق، الإدمان، الرهاب، النوم.",
    descriptionEn:
      "The hypnotherapist uses medical hypnosis to treat pain, anxiety, addictions, phobias, sleep issues.",
    commonConditions: ["Anxiété", "Insomnie", "Tabac", "Phobies", "Douleur chronique"],
    commonConditionsAr: ["القلق", "الأرق", "التدخين", "الرهاب", "الألم المزمن"],
    commonConditionsEn: ["Smoking cessation", "Stress", "Phobias", "Pain management", "Weight loss"],
    averagePriceMad: { min: 300, max: 800 },
  },
  // ─── Slugs paramedicaux & legacy (URLs scrapees, peu/pas en DB) ─────
  {
    slug: "opticien",
    nameFr: "Opticien-lunetier",
    nameAr: "نظّاراتي",
    nameEn: "Optician",
    icon: "👓",
    featured: false,
    descriptionFr:
      "L'opticien-lunetier réalise et ajuste vos lunettes et lentilles de contact à partir de la prescription de l'ophtalmologue. Conseil en monture et verres correcteurs.",
    descriptionAr:
      "يقوم النظاراتي بتصنيع وضبط النظارات والعدسات اللاصقة بناءً على وصفة طبيب العيون. تقديم النصح في اختيار الإطار والعدسات التصحيحية.",
    descriptionEn:
      "The optician fits and adjusts glasses and contact lenses based on the ophthalmologist's prescription. Advice on frames and corrective lenses.",
    commonConditions: ["Myopie", "Hypermétropie", "Presbytie", "Lentilles", "Lunettes"],
    commonConditionsAr: ["قصر النظر", "طول النظر", "قصو البصر الشيخوخي", "العدسات اللاصقة", "النظارات الطبية"],
    commonConditionsEn: ["Myopia", "Hyperopia", "Presbyopia", "Contact lenses", "Glasses"],
    averagePriceMad: { min: 200, max: 3000 },
  },
  {
    slug: "orthophoniste",
    nameFr: "Orthophoniste",
    nameAr: "أخصائي تقويم النطق",
    nameEn: "Speech therapist",
    icon: "🗣️",
    featured: false,
    descriptionFr:
      "L'orthophoniste rééduque les troubles du langage oral et écrit, de la voix, de la déglutition et de la communication : bégaiement, dyslexie, retard de parole, post-AVC.",
    descriptionAr:
      "يعيد أخصائي تقويم النطق تأهيل اضطرابات اللغة الشفوية والمكتوبة، الصوت، البلع والتواصل: التأتأة، عسر القراءة، تأخر الكلام، ما بعد الجلطة الدماغية.",
    descriptionEn:
      "The speech therapist treats disorders of spoken and written language, voice, swallowing and communication: stuttering, dyslexia, speech delay, post-stroke.",
    commonConditions: ["Bégaiement", "Dyslexie", "Retard de langage", "Troubles de la déglutition", "Rééducation post-AVC"],
    commonConditionsAr: ["التأتأة", "عسر القراءة", "تأخر الكلام", "اضطرابات البلع", "إعادة التأهيل بعد السكتة الدماغية"],
    commonConditionsEn: ["Dyslexia", "Stammering", "Language delay", "Dysphagia", "Post-stroke sequelae"],
    averagePriceMad: { min: 200, max: 400 },
  },
  // Pack V2 — paramédicaux + sous-spé + alternatives + chirurgies fines.
  // Concaté en bout de tableau pour minimiser le diff. Cf. specialties-v2.ts.
  ...SPECIALTIES_V2,
];

export const CITIES: CitySeed[] = [
  // Top 12 featured (grandes métropoles + CTR)
  { slug: "casablanca", nameFr: "Casablanca", nameAr: "الدار البيضاء", nameEn: "Casablanca", region: "Casablanca-Settat", featured: true, lat: 33.5731, lng: -7.5898 },
  { slug: "rabat", nameFr: "Rabat", nameAr: "الرباط", nameEn: "Rabat", region: "Rabat-Salé-Kénitra", featured: true, lat: 34.0209, lng: -6.8416 },
  { slug: "marrakech", nameFr: "Marrakech", nameAr: "مراكش", nameEn: "Marrakech", region: "Marrakech-Safi", featured: true, lat: 31.6295, lng: -7.9811 },
  { slug: "fes", nameFr: "Fès", nameAr: "فاس", nameEn: "Fez", region: "Fès-Meknès", featured: true, lat: 34.0181, lng: -5.0078 },
  { slug: "tanger", nameFr: "Tanger", nameAr: "طنجة", nameEn: "Tangier", region: "Tanger-Tétouan-Al Hoceïma", featured: true, lat: 35.7595, lng: -5.8340 },
  { slug: "agadir", nameFr: "Agadir", nameAr: "أكادير", nameEn: "Agadir", region: "Souss-Massa", featured: true, lat: 30.4278, lng: -9.5981 },
  { slug: "meknes", nameFr: "Meknès", nameAr: "مكناس", nameEn: "Meknes", region: "Fès-Meknès", featured: true, lat: 33.8935, lng: -5.5473 },
  { slug: "oujda", nameFr: "Oujda", nameAr: "وجدة", nameEn: "Oujda", region: "Oriental", featured: true, lat: 34.6814, lng: -1.9086 },
  { slug: "kenitra", nameFr: "Kénitra", nameAr: "القنيطرة", nameEn: "Kenitra", region: "Rabat-Salé-Kénitra", featured: true, lat: 34.2610, lng: -6.5802 },
  { slug: "tetouan", nameFr: "Tétouan", nameAr: "تطوان", nameEn: "Tetouan", region: "Tanger-Tétouan-Al Hoceïma", featured: true, lat: 35.5889, lng: -5.3626 },
  { slug: "sale", nameFr: "Salé", nameAr: "سلا", nameEn: "Sale", region: "Rabat-Salé-Kénitra", featured: true, lat: 34.0333, lng: -6.8000 },
  { slug: "mohammedia", nameFr: "Mohammedia", nameAr: "المحمدية", nameEn: "Mohammedia", region: "Casablanca-Settat", featured: true, lat: 33.6864, lng: -7.3833 },

  // Extension couverture nationale (non-featured mais recherchables)
  { slug: "el-jadida", nameFr: "El Jadida", nameAr: "الجديدة", nameEn: "El Jadida", region: "Casablanca-Settat", featured: false, lat: 33.2316, lng: -8.5007 },
  { slug: "safi", nameFr: "Safi", nameAr: "آسفي", nameEn: "Safi", region: "Marrakech-Safi", featured: false, lat: 32.2994, lng: -9.2372 },
  { slug: "beni-mellal", nameFr: "Béni Mellal", nameAr: "بني ملال", nameEn: "Beni Mellal", region: "Béni Mellal-Khénifra", featured: false, lat: 32.3393, lng: -6.3498 },
  { slug: "nador", nameFr: "Nador", nameAr: "الناظور", nameEn: "Nador", region: "Oriental", featured: false, lat: 35.1688, lng: -2.9335 },
  { slug: "al-hoceima", nameFr: "Al Hoceïma", nameAr: "الحسيمة", nameEn: "Al Hoceima", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.2517, lng: -3.9372 },
  { slug: "chefchaouen", nameFr: "Chefchaouen", nameAr: "شفشاون", nameEn: "Chefchaouen", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.1714, lng: -5.2697 },
  { slug: "larache", nameFr: "Larache", nameAr: "العرائش", nameEn: "Larache", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.1933, lng: -6.1562 },
  { slug: "ksar-el-kebir", nameFr: "Ksar El Kebir", nameAr: "القصر الكبير", nameEn: "Ksar El Kebir", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.0111, lng: -5.9061 },
  { slug: "taza", nameFr: "Taza", nameAr: "تازة", nameEn: "Taza", region: "Fès-Meknès", featured: false, lat: 34.2133, lng: -4.0108 },
  { slug: "ouarzazate", nameFr: "Ouarzazate", nameAr: "ورزازات", nameEn: "Ouarzazate", region: "Drâa-Tafilalet", featured: false, lat: 30.9189, lng: -6.8934 },
  { slug: "errachidia", nameFr: "Errachidia", nameAr: "الرشيدية", nameEn: "Errachidia", region: "Drâa-Tafilalet", featured: false, lat: 31.9314, lng: -4.4247 },
  { slug: "khouribga", nameFr: "Khouribga", nameAr: "خريبكة", nameEn: "Khouribga", region: "Béni Mellal-Khénifra", featured: false, lat: 32.8811, lng: -6.9063 },
  { slug: "settat", nameFr: "Settat", nameAr: "سطات", nameEn: "Settat", region: "Casablanca-Settat", featured: false, lat: 33.0016, lng: -7.6162 },
  { slug: "berrechid", nameFr: "Berrechid", nameAr: "برشيد", nameEn: "Berrechid", region: "Casablanca-Settat", featured: false, lat: 33.2654, lng: -7.5870 },
  { slug: "benslimane", nameFr: "Benslimane", nameAr: "بن سليمان", nameEn: "Benslimane", region: "Casablanca-Settat", featured: false, lat: 33.6189, lng: -7.1206 },
  { slug: "khemisset", nameFr: "Khémisset", nameAr: "الخميسات", nameEn: "Khemisset", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.8244, lng: -6.0669 },
  { slug: "temara", nameFr: "Témara", nameAr: "تمارة", nameEn: "Temara", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.9287, lng: -6.9067 },
  { slug: "skhirat", nameFr: "Skhirat", nameAr: "الصخيرات", nameEn: "Skhirat", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.8533, lng: -7.0403 },
  { slug: "ben-guerir", nameFr: "Ben Guérir", nameAr: "بن جرير", nameEn: "Ben Guerir", region: "Marrakech-Safi", featured: false, lat: 32.2367, lng: -7.9525 },
  { slug: "essaouira", nameFr: "Essaouira", nameAr: "الصويرة", nameEn: "Essaouira", region: "Marrakech-Safi", featured: false, lat: 31.5085, lng: -9.7595 },
  { slug: "taroudant", nameFr: "Taroudant", nameAr: "تارودانت", nameEn: "Taroudant", region: "Souss-Massa", featured: false, lat: 30.4703, lng: -8.8766 },
  { slug: "tiznit", nameFr: "Tiznit", nameAr: "تزنيت", nameEn: "Tiznit", region: "Souss-Massa", featured: false, lat: 29.6980, lng: -9.7323 },
  { slug: "inezgane", nameFr: "Inezgane", nameAr: "إنزكان", nameEn: "Inezgane", region: "Souss-Massa", featured: false, lat: 30.3556, lng: -9.5350 },
  { slug: "dakhla", nameFr: "Dakhla", nameAr: "الداخلة", nameEn: "Dakhla", region: "Dakhla-Oued Ed-Dahab", featured: false, lat: 23.6848, lng: -15.9580 },
  { slug: "laayoune", nameFr: "Laâyoune", nameAr: "العيون", nameEn: "Laayoune", region: "Laâyoune-Sakia El Hamra", featured: false, lat: 27.1500, lng: -13.2000 },
  { slug: "guelmim", nameFr: "Guelmim", nameAr: "كلميم", nameEn: "Guelmim", region: "Guelmim-Oued Noun", featured: false, lat: 28.9883, lng: -10.0568 },
  { slug: "ifrane", nameFr: "Ifrane", nameAr: "إفران", nameEn: "Ifrane", region: "Fès-Meknès", featured: false, lat: 33.5228, lng: -5.1106 },
  { slug: "azrou", nameFr: "Azrou", nameAr: "أزرو", nameEn: "Azrou", region: "Fès-Meknès", featured: false, lat: 33.4347, lng: -5.2214 },
  { slug: "sefrou", nameFr: "Sefrou", nameAr: "صفرو", nameEn: "Sefrou", region: "Fès-Meknès", featured: false, lat: 33.8306, lng: -4.8289 },
  { slug: "berkane", nameFr: "Berkane", nameAr: "بركان", nameEn: "Berkane", region: "Oriental", featured: false, lat: 34.9200, lng: -2.3200 },
  { slug: "taourirt", nameFr: "Taourirt", nameAr: "تاوريرت", nameEn: "Taourirt", region: "Oriental", featured: false, lat: 34.4069, lng: -2.8969 },
  { slug: "jerada", nameFr: "Jerada", nameAr: "جرادة", nameEn: "Jerada", region: "Oriental", featured: false, lat: 34.3100, lng: -2.1600 },
  { slug: "midelt", nameFr: "Midelt", nameAr: "ميدلت", nameEn: "Midelt", region: "Drâa-Tafilalet", featured: false, lat: 32.6800, lng: -4.7300 },
  { slug: "tinghir", nameFr: "Tinghir", nameAr: "تنغير", nameEn: "Tinghir", region: "Drâa-Tafilalet", featured: false, lat: 31.5147, lng: -5.5324 },
  { slug: "zagora", nameFr: "Zagora", nameAr: "زاكورة", nameEn: "Zagora", region: "Drâa-Tafilalet", featured: false, lat: 30.3286, lng: -5.8386 },
  { slug: "khenifra", nameFr: "Khénifra", nameAr: "خنيفرة", nameEn: "Khenifra", region: "Béni Mellal-Khénifra", featured: false, lat: 32.9394, lng: -5.6672 },
  { slug: "azilal", nameFr: "Azilal", nameAr: "أزيلال", nameEn: "Azilal", region: "Béni Mellal-Khénifra", featured: false, lat: 31.9614, lng: -6.5708 },
  { slug: "fquih-ben-salah", nameFr: "Fquih Ben Salah", nameAr: "الفقيه بن صالح", nameEn: "Fquih Ben Salah", region: "Béni Mellal-Khénifra", featured: false, lat: 32.5022, lng: -6.7053 },
  { slug: "oued-zem", nameFr: "Oued Zem", nameAr: "وادي زم", nameEn: "Oued Zem", region: "Béni Mellal-Khénifra", featured: false, lat: 32.8614, lng: -6.5678 },
  { slug: "sidi-slimane", nameFr: "Sidi Slimane", nameAr: "سيدي سليمان", nameEn: "Sidi Slimane", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.2644, lng: -5.9269 },
  { slug: "sidi-kacem", nameFr: "Sidi Kacem", nameAr: "سيدي قاسم", nameEn: "Sidi Kacem", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.2214, lng: -5.7075 },
  { slug: "tiflet", nameFr: "Tiflet", nameAr: "تيفلت", nameEn: "Tiflet", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.8936, lng: -6.3083 },
  { slug: "youssoufia", nameFr: "Youssoufia", nameAr: "اليوسفية", nameEn: "Youssoufia", region: "Marrakech-Safi", featured: false, lat: 32.2464, lng: -8.5289 },
  { slug: "el-kelaa-des-sraghna", nameFr: "El Kelaâ des Sraghna", nameAr: "قلعة السراغنة", nameEn: "El Kelaa des Sraghna", region: "Marrakech-Safi", featured: false, lat: 32.0500, lng: -7.4100 },
  { slug: "chichaoua", nameFr: "Chichaoua", nameAr: "شيشاوة", nameEn: "Chichaoua", region: "Marrakech-Safi", featured: false, lat: 31.5394, lng: -8.7658 },
  { slug: "sidi-ifni", nameFr: "Sidi Ifni", nameAr: "سيدي إفني", nameEn: "Sidi Ifni", region: "Guelmim-Oued Noun", featured: false, lat: 29.3797, lng: -10.1722 },
  { slug: "tan-tan", nameFr: "Tan-Tan", nameAr: "طانطان", nameEn: "Tan-Tan", region: "Guelmim-Oued Noun", featured: false, lat: 28.4381, lng: -11.1033 },
  { slug: "boujdour", nameFr: "Boujdour", nameAr: "بوجدور", nameEn: "Boujdour", region: "Laâyoune-Sakia El Hamra", featured: false, lat: 26.1256, lng: -14.4842 },
  { slug: "smara", nameFr: "Smara", nameAr: "السمارة", nameEn: "Smara", region: "Laâyoune-Sakia El Hamra", featured: false, lat: 26.7386, lng: -11.6725 },
  // « مرتيل » et non « مارتيل » : sur 90 j, GSC ne voit AUCUNE requête écrite
  // « مارتيل » (0 impression) contre 5 requêtes / 141 impressions / 4 clics
  // pour « مرتيل ». Le titre arabe affichait une graphie que personne ne tape.
  { slug: "martil", nameFr: "Martil", nameAr: "مرتيل", nameEn: "Martil", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.6167, lng: -5.2750 },
  { slug: "fnideq", nameFr: "Fnideq", nameAr: "الفنيدق", nameEn: "Fnideq", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.8486, lng: -5.3572 },
  { slug: "mdiq", nameFr: "M'diq", nameAr: "المضيق", nameEn: "Mdiq", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.6856, lng: -5.3233 },
  { slug: "asilah", nameFr: "Asilah", nameAr: "أصيلة", nameEn: "Asilah", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.4650, lng: -6.0336 },

  // Extension complète — villes secondaires, chefs-lieux de cercle, centres urbains
  { slug: "ait-melloul", nameFr: "Aït Melloul", nameAr: "أيت ملول", nameEn: "Ait Melloul", region: "Souss-Massa", featured: false, lat: 30.3422, lng: -9.4906 },
  { slug: "ain-harrouda", nameFr: "Ain Harrouda", nameAr: "عين حرودة", nameEn: "Ain Harrouda", region: "Casablanca-Settat", featured: false, lat: 33.6369, lng: -7.4478 },
  { slug: "ain-taoujdate", nameFr: "Ain Taoujdate", nameAr: "عين تاوجطات", nameEn: "Ain Taoujdate", region: "Fès-Meknès", featured: false, lat: 33.9350, lng: -5.2167 },
  { slug: "akhfennir", nameFr: "Akhfennir", nameAr: "أخفنير", nameEn: "Akhfennir", region: "Guelmim-Oued Noun", featured: false, lat: 28.1000, lng: -12.0667 },
  { slug: "aouli", nameFr: "Aoulouz", nameAr: "أولوز", nameEn: "Aoulouz", region: "Souss-Massa", featured: false, lat: 30.6806, lng: -8.1664 },
  { slug: "aourir", nameFr: "Aourir", nameAr: "أورير", nameEn: "Aourir", region: "Souss-Massa", featured: false, lat: 30.5147, lng: -9.6247 },
  { slug: "arfoud", nameFr: "Arfoud", nameAr: "أرفود", nameEn: "Arfoud", region: "Drâa-Tafilalet", featured: false, lat: 31.4319, lng: -4.2381 },
  { slug: "azemmour", nameFr: "Azemmour", nameAr: "أزمور", nameEn: "Azemmour", region: "Casablanca-Settat", featured: false, lat: 33.2873, lng: -8.3425 },
  { slug: "bejaad", nameFr: "Béjaâd", nameAr: "بجعد", nameEn: "Bejaad", region: "Béni Mellal-Khénifra", featured: false, lat: 32.7644, lng: -6.3778 },
  { slug: "beni-ansar", nameFr: "Beni Ansar", nameAr: "بني أنصار", nameEn: "Beni Ansar", region: "Oriental", featured: false, lat: 35.2661, lng: -2.9358 },
  { slug: "beni-enzar", nameFr: "Beni Enzar", nameAr: "بني انزار", nameEn: "Beni Enzar", region: "Oriental", featured: false, lat: 35.2714, lng: -2.9311 },
  { slug: "biougra", nameFr: "Biougra", nameAr: "بيوكرى", nameEn: "Biougra", region: "Souss-Massa", featured: false, lat: 30.2147, lng: -9.3706 },
  { slug: "bir-jdid", nameFr: "Bir Jdid", nameAr: "بئر الجديد", nameEn: "Bir Jdid", region: "Casablanca-Settat", featured: false, lat: 33.3833, lng: -8.1333 },
  { slug: "bouarfa", nameFr: "Bouarfa", nameAr: "بوعرفة", nameEn: "Bouarfa", region: "Oriental", featured: false, lat: 32.5314, lng: -1.9650 },
  { slug: "bouznika", nameFr: "Bouznika", nameAr: "بوزنيقة", nameEn: "Bouznika", region: "Casablanca-Settat", featured: false, lat: 33.7889, lng: -7.1586 },
  { slug: "dcheira", nameFr: "Dcheira El Jihadia", nameAr: "الدشيرة الجهادية", nameEn: "Dcheira", region: "Souss-Massa", featured: false, lat: 30.3658, lng: -9.5594 },
  { slug: "demnate", nameFr: "Demnate", nameAr: "دمنات", nameEn: "Demnate", region: "Béni Mellal-Khénifra", featured: false, lat: 31.9406, lng: -7.0989 },
  { slug: "driouch", nameFr: "Driouch", nameAr: "الدريوش", nameEn: "Driouch", region: "Oriental", featured: false, lat: 35.0006, lng: -3.3914 },
  { slug: "el-aioun", nameFr: "El Aïoun Sidi Mellouk", nameAr: "العيون سيدي ملوك", nameEn: "El Aioun", region: "Oriental", featured: false, lat: 34.5800, lng: -2.5111 },
  { slug: "el-hajeb", nameFr: "El Hajeb", nameAr: "الحاجب", nameEn: "El Hajeb", region: "Fès-Meknès", featured: false, lat: 33.6928, lng: -5.3717 },
  { slug: "figuig", nameFr: "Figuig", nameAr: "فكيك", nameEn: "Figuig", region: "Oriental", featured: false, lat: 32.1097, lng: -1.2294 },
  { slug: "goulmima", nameFr: "Goulmima", nameAr: "كلميمة", nameEn: "Goulmima", region: "Drâa-Tafilalet", featured: false, lat: 31.6844, lng: -4.9594 },
  { slug: "guercif", nameFr: "Guercif", nameAr: "جرسيف", nameEn: "Guercif", region: "Oriental", featured: false, lat: 34.2267, lng: -3.3528 },
  { slug: "imouzzer-kandar", nameFr: "Imouzzer Kandar", nameAr: "إموزار كندر", nameEn: "Imouzzer Kandar", region: "Fès-Meknès", featured: false, lat: 33.7333, lng: -5.0167 },
  { slug: "imzouren", nameFr: "Imzouren", nameAr: "إمزورن", nameEn: "Imzouren", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.1472, lng: -3.8492 },
  { slug: "jorf-el-melha", nameFr: "Jorf El Melha", nameAr: "جرف الملحة", nameEn: "Jorf El Melha", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.4267, lng: -5.8617 },
  { slug: "kalaat-mgouna", nameFr: "Kalaat M'Gouna", nameAr: "قلعة مكونة", nameEn: "Kalaat Mgouna", region: "Drâa-Tafilalet", featured: false, lat: 31.2419, lng: -6.1339 },
  { slug: "kasba-tadla", nameFr: "Kasba Tadla", nameAr: "قصبة تادلة", nameEn: "Kasba Tadla", region: "Béni Mellal-Khénifra", featured: false, lat: 32.5939, lng: -6.2614 },
  { slug: "lalla-mimouna", nameFr: "Lalla Mimouna", nameAr: "لالة ميمونة", nameEn: "Lalla Mimouna", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.8500, lng: -6.0833 },
  { slug: "mdiq-fnideq", nameFr: "M'diq-Fnideq", nameAr: "المضيق-الفنيدق", nameEn: "Mdiq-Fnideq", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 35.7417, lng: -5.3389 },
  { slug: "midar", nameFr: "Midar", nameAr: "ميضار", nameEn: "Midar", region: "Oriental", featured: false, lat: 34.9300, lng: -3.5431 },
  { slug: "missour", nameFr: "Missour", nameAr: "ميسور", nameEn: "Missour", region: "Fès-Meknès", featured: false, lat: 33.0500, lng: -3.9833 },
  { slug: "mohammed-v-airport", nameFr: "Nouaceur", nameAr: "النواصر", nameEn: "Nouaceur", region: "Casablanca-Settat", featured: false, lat: 33.3667, lng: -7.5833 },
  { slug: "moulay-bousselham", nameFr: "Moulay Bousselham", nameAr: "مولاي بوسلهام", nameEn: "Moulay Bousselham", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.8789, lng: -6.2936 },
  { slug: "moulay-yacoub", nameFr: "Moulay Yacoub", nameAr: "مولاي يعقوب", nameEn: "Moulay Yacoub", region: "Fès-Meknès", featured: false, lat: 34.0833, lng: -5.1833 },
  { slug: "mrirt", nameFr: "Mrirt", nameAr: "مريرت", nameEn: "Mrirt", region: "Béni Mellal-Khénifra", featured: false, lat: 33.1667, lng: -5.5667 },
  { slug: "ouad-amlil", nameFr: "Oued Amlil", nameAr: "واد أمليل", nameEn: "Oued Amlil", region: "Fès-Meknès", featured: false, lat: 34.1981, lng: -4.2836 },
  { slug: "ouazzane", nameFr: "Ouezzane", nameAr: "وزان", nameEn: "Ouezzane", region: "Tanger-Tétouan-Al Hoceïma", featured: false, lat: 34.7950, lng: -5.5806 },
  { slug: "ouled-teima", nameFr: "Ouled Teima", nameAr: "أولاد تايمة", nameEn: "Ouled Teima", region: "Souss-Massa", featured: false, lat: 30.3906, lng: -9.2122 },
  { slug: "oulmes", nameFr: "Oulmès", nameAr: "أولماس", nameEn: "Oulmes", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.4300, lng: -6.0050 },
  { slug: "rissani", nameFr: "Rissani", nameAr: "الريصاني", nameEn: "Rissani", region: "Drâa-Tafilalet", featured: false, lat: 31.2836, lng: -4.2739 },
  { slug: "sale-al-jadida", nameFr: "Salé Al Jadida", nameAr: "سلا الجديدة", nameEn: "Sale Al Jadida", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.0250, lng: -6.7167 },
  { slug: "sidi-bennour", nameFr: "Sidi Bennour", nameAr: "سيدي بنور", nameEn: "Sidi Bennour", region: "Casablanca-Settat", featured: false, lat: 32.6517, lng: -8.4456 },
  { slug: "sidi-bouzid", nameFr: "Sidi Bouzid", nameAr: "سيدي بوزيد", nameEn: "Sidi Bouzid", region: "Casablanca-Settat", featured: false, lat: 33.1500, lng: -8.3833 },
  { slug: "sidi-rahhal", nameFr: "Sidi Rahhal", nameAr: "سيدي رحال", nameEn: "Sidi Rahhal", region: "Marrakech-Safi", featured: false, lat: 31.6236, lng: -7.4931 },
  { slug: "sidi-yahya-el-gharb", nameFr: "Sidi Yahya El Gharb", nameAr: "سيدي يحيى الغرب", nameEn: "Sidi Yahya El Gharb", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.3094, lng: -6.3092 },
  { slug: "skoura", nameFr: "Skoura", nameAr: "سكورة", nameEn: "Skoura", region: "Drâa-Tafilalet", featured: false, lat: 31.0594, lng: -6.5547 },
  { slug: "souk-el-arbaa", nameFr: "Souk El Arbaa", nameAr: "سوق الأربعاء", nameEn: "Souk El Arbaa", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.6858, lng: -5.9978 },
  { slug: "souk-sebt", nameFr: "Souk Sebt Oulad Nemma", nameAr: "سوق السبت أولاد النمة", nameEn: "Souk Sebt", region: "Béni Mellal-Khénifra", featured: false, lat: 32.3000, lng: -6.6833 },
  { slug: "tahannaout", nameFr: "Tahannaout", nameAr: "تحناوت", nameEn: "Tahannaout", region: "Marrakech-Safi", featured: false, lat: 31.3514, lng: -7.9458 },
  { slug: "tamaris", nameFr: "Dar Bouazza", nameAr: "دار بوعزة", nameEn: "Dar Bouazza", region: "Casablanca-Settat", featured: false, lat: 33.5361, lng: -7.8142 },
  { slug: "tamanar", nameFr: "Tamanar", nameAr: "تمنار", nameEn: "Tamanar", region: "Marrakech-Safi", featured: false, lat: 31.0000, lng: -9.6833 },
  { slug: "taounate", nameFr: "Taounate", nameAr: "تاونات", nameEn: "Taounate", region: "Fès-Meknès", featured: false, lat: 34.5367, lng: -4.6408 },
  { slug: "temara-harhoura", nameFr: "Harhoura", nameAr: "الهرهورة", nameEn: "Harhoura", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.9089, lng: -6.9550 },
  { slug: "tifelt", nameFr: "Thifelt", nameAr: "ثيفلت", nameEn: "Thifelt", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.8936, lng: -6.3083 },
  { slug: "tinejdad", nameFr: "Tinejdad", nameAr: "تنجداد", nameEn: "Tinejdad", region: "Drâa-Tafilalet", featured: false, lat: 31.5167, lng: -5.0667 },
  { slug: "tit-mellil", nameFr: "Tit Mellil", nameAr: "تيط مليل", nameEn: "Tit Mellil", region: "Casablanca-Settat", featured: false, lat: 33.5500, lng: -7.4833 },
  { slug: "zaio", nameFr: "Zaïo", nameAr: "الزايو", nameEn: "Zaio", region: "Oriental", featured: false, lat: 34.9378, lng: -2.7289 },
  { slug: "zeghanghane", nameFr: "Zeghanghane", nameAr: "زغنغان", nameEn: "Zeghanghane", region: "Oriental", featured: false, lat: 35.1594, lng: -2.9725 },

  // Ajouts pour correspondance annuaire-gratuit.ma (pharmacies de garde)
  { slug: "al-arrui", nameFr: "Al Arrui", nameAr: "العروي", nameEn: "Al Arrui", region: "Oriental", featured: false, lat: 35.0167, lng: -2.9500 },
  { slug: "ben-slimane", nameFr: "Ben Slimane", nameAr: "بن سليمان", nameEn: "Ben Slimane", region: "Casablanca-Settat", featured: false, lat: 33.6178, lng: -7.1219 },
  { slug: "boujad", nameFr: "Boujad", nameAr: "بوجعد", nameEn: "Boujad", region: "Béni Mellal-Khénifra", featured: false, lat: 32.7647, lng: -6.3792 },
  { slug: "mechraa-bel-ksiri", nameFr: "Mechraâ Bel Ksiri", nameAr: "مشرع بلقصيري", nameEn: "Mechraa Bel Ksiri", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.5731, lng: -5.9583 },
  { slug: "selouane", nameFr: "Selouane", nameAr: "سلوان", nameEn: "Selouane", region: "Oriental", featured: false, lat: 35.0758, lng: -2.9258 },
  { slug: "souk-el-arba-du-gharb", nameFr: "Souk El Arba du Gharb", nameAr: "سوق الأربعاء الغرب", nameEn: "Souk El Arba du Gharb", region: "Rabat-Salé-Kénitra", featured: false, lat: 34.6878, lng: -5.9928 },
  { slug: "tahla", nameFr: "Tahla", nameAr: "تاهلة", nameEn: "Tahla", region: "Fès-Meknès", featured: false, lat: 34.0517, lng: -4.4244 },
  { slug: "tikiouine", nameFr: "Tikiouine", nameAr: "تكوين", nameEn: "Tikiouine", region: "Souss-Massa", featured: false, lat: 30.3500, lng: -9.4833 },
  // Villes ajoutées 2026-05-22 — vraies villes marocaines présentes en DB comme
  // cityId mais absentes du canon → leurs médecins tombaient en fallback
  // /medecin/[slug] (pas d'URL Doctolib propre). Source : audit table City vs
  // CITIES (fiches rattachées). Coords = centroïde ville.
  { slug: "mediouna", nameFr: "Médiouna", nameAr: "مديونة", nameEn: "Mediouna", region: "Casablanca-Settat", featured: false, lat: 33.4503, lng: -7.5167 },
  { slug: "bouskoura", nameFr: "Bouskoura", nameAr: "بوسكورة", nameEn: "Bouskoura", region: "Casablanca-Settat", featured: false, lat: 33.4486, lng: -7.6486 },
  { slug: "boulemane", nameFr: "Boulemane", nameAr: "بولمان", nameEn: "Boulemane", region: "Fès-Meknès", featured: false, lat: 33.3667, lng: -4.7300 },
  { slug: "deroua", nameFr: "Deroua", nameAr: "الدروة", nameEn: "Deroua", region: "Casablanca-Settat", featured: false, lat: 33.2922, lng: -7.4708 },
  { slug: "tata", nameFr: "Tata", nameAr: "طاطا", nameEn: "Tata", region: "Souss-Massa", featured: false, lat: 29.7500, lng: -7.9722 },
  { slug: "had-soualem", nameFr: "Had Soualem", nameAr: "حد السوالم", nameEn: "Had Soualem", region: "Casablanca-Settat", featured: false, lat: 33.3833, lng: -7.8500 },
  { slug: "benguerir", nameFr: "Ben Guerir", nameAr: "ابن جرير", nameEn: "Ben Guerir", region: "Marrakech-Safi", featured: false, lat: 32.2361, lng: -7.9508 },
  { slug: "tamesna", nameFr: "Tamesna", nameAr: "تامسنا", nameEn: "Tamesna", region: "Rabat-Salé-Kénitra", featured: false, lat: 33.7000, lng: -6.9167 },
  // Ajouts 2026-06-22 — slugs canoniques complets pour villes déjà présentes
  // sous forme courte (souk-sebt, mohammed-v-airport). Les alias correspondants
  // sont déclarés dans url-builder.ts CITY_ALIASES pour rediriger l'ancien
  // slug vers le canonique.
  { slug: "souk-sebt-oulad-nemma", nameFr: "Souk Sebt Oulad Nemma", nameAr: "سوق السبت أولاد النمة", nameEn: "Souk Sebt Oulad Nemma", region: "Béni Mellal-Khénifra", featured: false, lat: 32.3000, lng: -6.6833 },
  { slug: "nouaceur", nameFr: "Nouaceur", nameAr: "النواصر", nameEn: "Nouaceur", region: "Casablanca-Settat", featured: false, lat: 33.3667, lng: -7.5833 },
];

export function getSpecialty(slug: string): SpecialtySeed | undefined {
  return SPECIALTIES.find((s) => s.slug === slug);
}

export function getCity(slug: string): CitySeed | undefined {
  return CITIES.find((c) => c.slug === slug);
}
